import React from 'react'
import { styled } from '@mui/material'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useSortablePages } from './hooks/useSortablePages'
import { PageThumbnailItem } from './components/PageThumbnailItem'
import { VersionReportTemplatePageFormModel } from '../../model/VersionReportTemplatePageFormModel'

type PageThumbnailListProps = {
  pages: VersionReportTemplatePageFormModel[]
  selectedPageNumber: number | null
  pageErrorMap: Map<number, boolean>
  onSelectPage: (pageNumber: number) => void
  onDeletePage: (pageNumber: number) => void
  onReorderPages: (reorderedPages: VersionReportTemplatePageFormModel[]) => void
}

export const PageThumbnailList: React.FC<PageThumbnailListProps> = ({
  pages,
  selectedPageNumber,
  pageErrorMap,
  onSelectPage,
  onDeletePage,
  onReorderPages,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const { handleDragStart, handleDragEnd, handleDragCancel } = useSortablePages(
    {
      items: pages,
      onReorder: onReorderPages,
      getId: page => page.pageNumber,
    }
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={pages.map(page => page.pageNumber)}
        strategy={verticalListSortingStrategy}
      >
        <ThumbnailContainer>
          {pages.map(page => (
            <PageThumbnailItem
              key={page.pageNumber}
              page={page}
              isSelected={page.pageNumber === selectedPageNumber}
              hasError={pageErrorMap.get(page.pageNumber) ?? false}
              onSelect={() => onSelectPage(page.pageNumber)}
              onDelete={() => onDeletePage(page.pageNumber)}
            />
          ))}
        </ThumbnailContainer>
      </SortableContext>
    </DndContext>
  )
}

const ThumbnailContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px',
})
